import React, { useState, useEffect } from 'react';
import mammoth from 'mammoth';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as DocumentIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface CompanyDocumentsProps {
  companyId: number;
  canWrite: boolean;
}

const CompanyDocuments: React.FC<CompanyDocumentsProps> = ({ companyId, canWrite }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [uploadData, setUploadData] = useState({
    document_type: 'certificate',
    notes: '',
    expiry_date: ''
  });

  const [documentTypes, setDocumentTypes] = useState<{ value: string; label: string; type_id?: number; is_global?: boolean }[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [typeManageOpen, setTypeManageOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const [editingTypeName, setEditingTypeName] = useState('');
  const [savingType, setSavingType] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordHtml, setWordHtml] = useState<string | null>(null);

  useEffect(() => {
    if (companyId) {
      fetchDocuments();
      fetchDocumentTypes();
    }
  }, [companyId]);

  const fetchDocumentTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await fetch(apiUrl(`/api/document-types/company/${companyId}`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const types = data.document_types.map((t: any) => ({
          value: t.type_value,
          label: t.type_label,
          type_id: t.type_id,
          is_global: t.is_global
        }));
        setDocumentTypes(types);
      } else {
        // Fallback to default types if API fails
        setDocumentTypes([
          { value: 'certificate', label: 'Certificate', is_global: true },
          { value: 'license', label: 'License', is_global: true },
          { value: 'insurance', label: 'Insurance', is_global: true },
          { value: 'contract', label: 'Contract', is_global: true },
          { value: 'financial', label: 'Financial Document', is_global: true },
          { value: 'other', label: 'Other', is_global: true }
        ]);
      }
    } catch (err) {
      // Fallback to default types
      setDocumentTypes([
        { value: 'certificate', label: 'Certificate', is_global: true },
        { value: 'license', label: 'License', is_global: true },
        { value: 'insurance', label: 'Insurance', is_global: true },
        { value: 'contract', label: 'Contract', is_global: true },
        { value: 'financial', label: 'Financial Document', is_global: true },
        { value: 'other', label: 'Other', is_global: true }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl(`/api/companies/${companyId}/documents`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        throw new Error('Failed to fetch documents');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setUploadData({
      document_type: 'certificate',
      notes: '',
      expiry_date: ''
    });
    setSelectedFile(null);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadData.document_type);
      if (uploadData.notes) formData.append('notes', uploadData.notes);
      if (uploadData.expiry_date) formData.append('expiry_date', uploadData.expiry_date);

      const response = await fetch(apiUrl(`/api/companies/${companyId}/documents`), {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        await fetchDocuments();
        handleUploadDialogClose();
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: any) => {
    try {
      // Check if file_path is an Azure URL
      if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
        // Direct download from Azure - open in new tab (no CORS issues)
        window.open(document.file_path, '_blank');
      } else {
        // Fallback: use API endpoint for old local files
        const response = await fetch(
          apiUrl(`/api/companies/${companyId}/documents/${document.file_path.split('/').pop()}`),
          { credentials: 'include' }
        );

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = window.document.createElement('a');
          a.href = url;
          a.download = document.document_name;
          window.document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          window.document.body.removeChild(a);
        } else {
          throw new Error('Failed to download document');
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(apiUrl(`/api/companies/${companyId}/documents/${documentId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchDocuments();
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };


  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const isExpiringSoon = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  const handleAddType = async () => {
    if (!newTypeName.trim() || addingType) return;

    try {
      setAddingType(true);
      const typeValue = newTypeName.toLowerCase().replace(/\s+/g, '_');

      const response = await fetch(apiUrl('/api/document-types'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          type_value: typeValue,
          type_label: newTypeName.trim()
        })
      });

      if (response.ok) {
        await fetchDocumentTypes();
        setNewTypeName('');
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add document type');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingType(false);
    }
  };

  const handleDeleteType = async (typeId: number) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) return;

    try {
      const response = await fetch(apiUrl(`/api/document-types/${typeId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchDocumentTypes();
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document type');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleStartEditType = (typeId: number, currentLabel: string) => {
    setEditingTypeId(typeId);
    setEditingTypeName(currentLabel);
  };

  const handleCancelEditType = () => {
    setEditingTypeId(null);
    setEditingTypeName('');
  };

  const handleSaveEditType = async () => {
    if (!editingTypeId || !editingTypeName.trim() || savingType) return;

    try {
      setSavingType(true);
      const typeValue = editingTypeName.toLowerCase().replace(/\s+/g, '_');

      const response = await fetch(apiUrl(`/api/document-types/${editingTypeId}`), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type_value: typeValue,
          type_label: editingTypeName.trim()
        })
      });

      if (response.ok) {
        await fetchDocumentTypes();
        setEditingTypeId(null);
        setEditingTypeName('');
        setError(null);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update document type');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingType(false);
    }
  };

  const handlePreview = async (document: any) => {
    try {
      setPreviewLoading(true);
      setPreviewDocument(document);
      setPreviewOpen(true);
      setWordHtml(null);
      setPreviewUrl(null);

      // Check if it's a Word document
      const isWord = document.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                     document.mime_type === 'application/msword';

      if (isWord) {
        // For Word docs, fetch as blob and convert
        let blob;
        if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
          // Fetch from Azure
          const response = await fetch(document.file_path);
          if (!response.ok) throw new Error('Failed to load document');
          blob = await response.blob();
        } else {
          // Fetch from local API
          const response = await fetch(
            apiUrl(`/api/companies/${companyId}/documents/${document.file_path.split('/').pop()}`),
            { credentials: 'include' }
          );
          if (!response.ok) throw new Error('Failed to load document');
          blob = await response.blob();
        }

        // Convert Word to HTML using mammoth
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setWordHtml(result.value);
      } else {
        // For PDFs and images, use URL directly
        if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
          // Directly use Azure URL (no CORS issues for img/iframe)
          setPreviewUrl(document.file_path);
        } else {
          // Fallback: use API endpoint for old local files
          const response = await fetch(
            apiUrl(`/api/companies/${companyId}/documents/${document.file_path.split('/').pop()}`),
            { credentials: 'include' }
          );

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
          } else {
            throw new Error('Failed to load preview');
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
    setWordHtml(null);
    if (previewUrl) {
      // Only revoke if it's a blob URL, not an Azure URL
      if (previewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }
  };

  const isPreviewable = (mimeType: string | null) => {
    if (!mimeType) return false;
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || // .docx
      mimeType === 'application/msword' // .doc
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Company Documents
        </Typography>
        <Box display="flex" gap={1}>
          {canWrite && (
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setTypeManageOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Manage Types
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUploadDialogOpen}
                sx={{ borderRadius: 2 }}
              >
                Upload Document
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Document Name</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Expiry Date</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Uploaded</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      No documents uploaded yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc: any) => (
                  <TableRow
                    key={doc.document_id}
                    sx={{
                      '&:hover': { bgcolor: 'grey.50' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                        {doc.document_name}
                      </Typography>
                      {doc.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {doc.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={doc.document_type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: '0.875rem' }}>
                      {formatFileSize(doc.file_size)}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: '0.875rem' }}>
                      v{doc.version}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {doc.expiry_date && (
                        <Chip
                          label={formatDate(doc.expiry_date)}
                          size="small"
                          color={
                            isExpired(doc.expiry_date)
                              ? 'error'
                              : isExpiringSoon(doc.expiry_date)
                              ? 'warning'
                              : 'default'
                          }
                        />
                      )}
                      {!doc.expiry_date && (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: '0.875rem' }}>
                      {formatDate(doc.created_at)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {isPreviewable(doc.mime_type) && (
                          <IconButton
                            onClick={() => handlePreview(doc)}
                            color="info"
                            size="small"
                            title="Preview"
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => handleDownload(doc)}
                          color="primary"
                          size="small"
                          title="Download"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        {canWrite && (
                          <IconButton
                            onClick={() => handleDelete(doc.document_id)}
                            color="error"
                            size="small"
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Choose File'}
              <input type="file" hidden onChange={handleFileSelect} />
            </Button>

            <TextField
              select
              label="Document Type *"
              value={uploadData.document_type}
              onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              fullWidth
              size="small"
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Expiry Date"
              type="date"
              value={uploadData.expiry_date}
              onChange={(e) => setUploadData({ ...uploadData, expiry_date: e.target.value })}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="Notes"
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Add any notes or description..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Document Types Dialog */}
      <Dialog open={typeManageOpen} onClose={() => setTypeManageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Document Types</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Add Custom Type for This Company
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Enter type name"
                size="small"
                fullWidth
                onKeyPress={(e) => e.key === 'Enter' && handleAddType()}
              />
              <Button
                variant="contained"
                onClick={handleAddType}
                disabled={!newTypeName.trim() || addingType}
                startIcon={addingType ? <CircularProgress size={16} /> : <AddIcon />}
              >
                Add
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Available Document Types
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Type Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentTypes.map((type) => (
                  <TableRow key={type.type_id || type.value}>
                    <TableCell>
                      {editingTypeId === type.type_id ? (
                        <TextField
                          value={editingTypeName}
                          onChange={(e) => setEditingTypeName(e.target.value)}
                          size="small"
                          fullWidth
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveEditType();
                            }
                          }}
                        />
                      ) : (
                        <span>{type.label}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={type.is_global ? 'Global' : 'Company'}
                        size="small"
                        color={type.is_global ? 'default' : 'primary'}
                        variant="outlined"
                        sx={{ pointerEvents: 'none' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {!type.is_global && type.type_id && (
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {editingTypeId === type.type_id ? (
                            <>
                              <IconButton
                                onClick={handleSaveEditType}
                                color="primary"
                                size="small"
                                title="Save"
                                disabled={savingType}
                              >
                                {savingType ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                              <IconButton
                                onClick={handleCancelEditType}
                                color="default"
                                size="small"
                                title="Cancel"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                onClick={() => type.type_id && handleStartEditType(type.type_id, type.label)}
                                color="primary"
                                size="small"
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => type.type_id && handleDeleteType(type.type_id)}
                                color="error"
                                size="small"
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )}
                      {type.is_global && (
                        <Typography variant="caption" color="text.secondary">
                          (Default)
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Global types are defaults for all companies. You can edit and delete company-specific types only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeManageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            {previewDocument?.document_name || 'Document Preview'}
          </Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100' }}>
          {previewLoading ? (
            <CircularProgress />
          ) : wordHtml ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                p: 4,
                bgcolor: 'white'
              }}
              dangerouslySetInnerHTML={{ __html: wordHtml }}
            />
          ) : previewUrl ? (
            previewDocument?.mime_type?.startsWith('image/') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={previewDocument?.document_name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : previewDocument?.mime_type === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title={previewDocument?.document_name}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            ) : (
              <Typography color="text.secondary">
                Preview not available for this file type
              </Typography>
            )
          ) : (
            <Typography color="text.secondary">
              Loading preview...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => previewDocument && handleDownload(previewDocument)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyDocuments;
